// ─────────────────────────────────────────────────────────────
// Shared — renderAll, item CRUD, attachments, global registry
// ─────────────────────────────────────────────────────────────
import { ref, set, get, remove }            from 'firebase/database';
import { ref as sRef, uploadBytesResumable,
         getDownloadURL, deleteObject }      from 'firebase/storage';
import { db, storage }                       from '@/firebase';
import { getState, getTrip }                 from '@/store/tripState';
import { saveTrip }                          from '@/services/sync.service';
import { openModal as uiOpenModal }          from '@/ui/modal';
import { toastSuccess }                      from '@/ui/toast';
import { logAction }                         from '@/services/logger.service';
import { g, gf, uid, toArr, fmtMask,
         diffDays, onBudgetInput,
         sanitize, requireFields }         from '@/helpers';
import { TRANSPORT_TYPES, STATUS_OPTIONS,
         CHECK_CATS, COMPRA_CATS,
         COTACAO_CATS, COTACAO_CAT_LABELS,
         COTACAO_STATUS, AGENDA_CATS,
         AGENDA_STATUS, AGENDA_CAT_ICONS,
         COMMON_CURRENCIES }            from '@/constants';
import { getActiveTab, onTabActivate }        from '@/ui/navigation';
import { renderSummary, renderTimeline }     from '@/tabs/overview';
import { setAgendaView, filterAgenda,
         clearAgendaFilters,
         toggleAgendaDay,
         toggleAgendaFilters }              from '@/tabs/agenda';
import { renderGastos, openGastoModal,
         filterGastos, clearGastosFilters } from '@/tabs/gastos';
import { renderDestinos, openDayTripModal,
         delDayTrip,
         toggleDestinoTrips }              from '@/tabs/destinos';
import { renderTransporte }                  from '@/tabs/transporte';
import { renderHoteis }                      from '@/tabs/hoteis';
import { renderAgenda, toggleAgenda }        from '@/tabs/agenda';
import { renderChecklist, toggleCheck }      from '@/tabs/checklist';
import { renderBudget, saveBudgetLimit }     from '@/tabs/orcamento';
import { renderCotacoes, toggleCompare,
         clearCompare, openCompareModal,
         updateCotacaoFields,
         buildCotacaoExtraFields }           from '@/tabs/cotacoes';
import { renderCompras, toggleCompra }       from '@/tabs/compras';
import { renderDocs, switchDocsTab,
         openDocModal, delGlobalDoc,
         openDocUrl }                        from '@/tabs/documentos';
import { renderTripSettings, saveTripField,
         getTravelerOptions,
         autoFillTravelerName }              from '@/tabs/viagem';
import { renderExportScreen,
         registerExportGlobals }              from '@/tabs/exportar';
import { renderInviteCard, generateInviteCode,
         revokeInviteCode, copyInviteCode,
         joinWithCode }                      from '@/tabs/invite';
import { attachmentViewerUrl }               from '@/ui/attachmentViewer';
import type { Trip, Traveler, Attachment,
              CotacaoCat }                   from '@/types';

// ── Lazy render infrastructure ────────────────────────────────
const _dirty = new Set<string>();
const ALL_TABS = ['overview', 'roteiro', 'gastos', 'planejar', 'documentos', 'exportar', 'viagem'];

// Sub-tab active state: which sub-tab is visible within each primary tab
const _subTabState: Record<string, string> = {
  roteiro:  'agenda',
  planejar: 'checklist',
};

// FAB label mapping (undefined = hide FAB)
const _fabLabels: Record<string, string> = {
  agenda:     'Atividade',
  destinos:   'Destino',
  transporte: 'Transporte',
  hoteis:     'Hotel',
  gastos:     'Gasto',
  checklist:  'Item',
  compras:    'Compra',
  documentos: 'Documento',
  cotacoes:   'Cotação',
};

export function updateFAB(context: string): void {
  const fab   = document.getElementById('fab-btn');
  const label = document.getElementById('fab-label');
  const lbl   = _fabLabels[context];
  if (fab)   fab.style.display = lbl ? 'flex' : 'none';
  if (label && lbl) label.textContent = lbl;
}

export function fabAction(): void {
  const active = getActiveTab() as string;
  let ctx = active === 'roteiro'  ? (_subTabState['roteiro']  ?? 'agenda')
          : active === 'planejar' ? (_subTabState['planejar'] ?? 'checklist')
          : active;
  switch (ctx) {
    case 'agenda':     openItemModal('agenda');     break;
    case 'destinos':   openItemModal('destino');    break;
    case 'transporte': openItemModal('transporte'); break;
    case 'hoteis':     openItemModal('hotel');      break;
    case 'gastos':     openGastoModal();             break;
    case 'checklist':  openItemModal('check');      break;
    case 'compras':    openItemModal('compra');     break;
    case 'documentos': openDocModal('viagem');      break;
    case 'cotacoes':   openItemModal('cotacao');    break;
  }
}

function renderForSubTab(sub: string): void {
  switch (sub) {
    case 'agenda':     renderAgenda();     break;
    case 'destinos':   renderDestinos();   break;
    case 'transporte': renderTransporte(); break;
    case 'hoteis':     renderHoteis();     break;
    case 'checklist':  renderChecklist();  break;
    case 'compras':    renderCompras();    break;
    case 'documentos': renderDocs();       break;
    case 'cotacoes':   renderCotacoes();   break;
  }
}

function activateSubTab(primaryTab: string, subTab: string): void {
  _subTabState[primaryTab] = subTab;
  document.querySelectorAll<HTMLElement>(`.subtab-pill[data-parent="${primaryTab}"]`).forEach(btn => {
    const on = btn.dataset.subtab === subTab;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-selected', String(on));
  });
  document.querySelectorAll<HTMLElement>(`.subtab-section[data-parent="${primaryTab}"]`).forEach(sec => {
    sec.classList.toggle('active', sec.dataset.subtab === subTab);
  });
  renderForSubTab(subTab);
  updateFAB(subTab);
}

export function switchSubTab(primaryTab: string, subTab: string): void {
  activateSubTab(primaryTab, subTab);
}

function renderForTab(tabId: string): void {
  try {
    switch (tabId) {
      case 'overview':
        renderSummary(); renderTimeline();
        updateFAB('overview');
        break;
      case 'roteiro':
        activateSubTab('roteiro', _subTabState['roteiro'] ?? 'agenda');
        break;
      case 'gastos':
        renderGastos(); renderBudget();
        updateFAB('gastos');
        break;
      case 'planejar':
        activateSubTab('planejar', _subTabState['planejar'] ?? 'checklist');
        break;
      case 'documentos':
        renderDocs();
        updateFAB('documentos');
        break;
      case 'exportar':
        renderExportScreen();
        updateFAB('exportar');
        break;
      case 'viagem':
        renderTripSettings(); renderCurrenciesList(); renderInviteCard();
        updateFAB('viagem');
        break;
    }
  } catch (err) {
    console.error(`[renderForTab] erro na aba "${tabId}":`, err);
    const panel = document.getElementById(`tab-${tabId}`);
    if (panel) {
      const errDiv = document.createElement('div');
      errDiv.style.cssText = 'margin:16px;padding:14px;background:var(--lux-red,#e53e3e);color:#fff;border-radius:8px;font-size:13px';
      errDiv.textContent   = 'Erro ao carregar esta seção. Tente atualizar a página.';
      panel.prepend(errDiv);
    }
  }
  _dirty.delete(tabId);
}


// ── Render only the active tab; mark others dirty ─────────────
export function renderAll(): void {
  const trip  = getTrip();
  const navEl = document.getElementById('nav-trip-title');
  if (navEl) navEl.textContent = `${trip.emoji ?? '✈️'} ${trip.name ?? ''}`;

  const active = getActiveTab();
  renderForTab(active);
  ALL_TABS.forEach(t => { if (t !== active) _dirty.add(t); });
}

// ── Delete item ───────────────────────────────────────────────
export async function delItem(path: string, id: string): Promise<void> {
  const { confirmModal } = await import('@/ui/modal');
  const ok = await confirmModal('Remover este item?');
  if (!ok) return;
  if (path === 'travelers') {
    const { isAdmin, currentUser } = getState();
    const tr = getTrip().travelers?.[id] as Traveler | undefined;
    if (!isAdmin && tr?.uid && tr.uid !== currentUser?.uid) {
      const { toastError } = await import('@/ui/toast');
      toastError('Você só pode remover a si mesmo da viagem.');
      return;
    }
    const tripId = getState().currentTripId;
    if (tr?.uid && tripId) await remove(ref(db, `userTrips/${tr.uid}/${tripId}`));
  }
  await saveTrip(`${path}/${id}`, null);
}

// ── Open modal for any item type ──────────────────────────────
export async function openItemModal(type: string, editId?: string): Promise<void> {
  const COLL: Record<string, keyof Trip> = {
    destino: 'destinos', transporte: 'transporte', hotel: 'hoteis',
    agenda: 'agenda', check: 'checks', traveler: 'travelers',
    cotacao: 'cotacoes', compra: 'compras',
  };
  const collKey = COLL[type] as keyof Trip;
  const trip    = getTrip();
  const coll    = trip[collKey] as Record<string, unknown> | undefined;
  const item    = editId && coll ? coll[editId] as Record<string, unknown> : null;
  const p       = (f: string, def = '') => {
    const raw = item ? (item[f] !== undefined ? String(item[f]) : def) : def;
    return sanitize(raw);
  };
  const isEdit  = !!item;
  const cur     = (trip.currencies as string[] | undefined)?.[0] ?? 'BRL';

  const LABELS: Record<string, string> = {
    destino: 'Destino', transporte: 'Transporte', hotel: 'Hotel',
    agenda: 'Item da Agenda', check: 'Item do Checklist',
    traveler: 'Viajante', cotacao: 'Cotação', compra: 'Item de Compra',
  };

  let body = '';

  if (type === 'destino') body = `
    <div class="r2">
      <div class="fg"><label>Cidade</label><input id="f-cidade" value="${p('cidade')}"></div>
      <div class="fg"><label>País</label><input id="f-pais" value="${p('pais')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Chegada</label><input type="date" id="f-chegada" value="${p('chegada')}"></div>
      <div class="fg"><label>Partida</label><input type="date" id="f-saida" value="${p('saida')}"></div>
    </div>
    <div class="fg"><label>Notas</label><textarea id="f-notas">${p('notas')}</textarea></div>`;

  if (type === 'transporte') body = `
    <div class="r2">
      <div class="fg"><label>Tipo</label><select id="f-tipo">
        ${TRANSPORT_TYPES.map(t =>
          `<option value="${t}" ${p('tipo')===t?'selected':''}>${t}</option>`).join('')}
      </select></div>
      <div class="fg"><label>Status</label><select id="f-status">
        ${STATUS_OPTIONS.map(s =>
          `<option value="${s}" ${p('status')===s?'selected':''}>${s.charAt(0).toUpperCase()+s.slice(1)}</option>`).join('')}
      </select></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Origem</label><input id="f-origem" value="${p('origem')}"></div>
      <div class="fg"><label>Destino</label><input id="f-destino" value="${p('destino')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Data da partida</label><input type="date" id="f-data" value="${p('data')}"></div>
      <div class="fg"><label>Data da chegada</label><input type="date" id="f-data-chegada" value="${p('dataChegada')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Hora da partida</label><input type="time" id="f-hora-partida" value="${p('horaPartida') || p('hora')}"></div>
      <div class="fg"><label>Hora da chegada</label><input type="time" id="f-hora-chegada" value="${p('horaChegada')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Operadora</label><input id="f-operadora" value="${p('operadora')}"></div>
      <div class="fg"><label>Número</label><input id="f-num" value="${p('num')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Preço (${cur})</label><input type="text" inputmode="numeric" id="f-preco"
        value="${p('preco') ? fmtMask(Number(p('preco'))) : ''}" oninput="window.__onBudgetInput(this)" placeholder="0,00"></div>
      <div class="fg"><label>Quem pagou</label><select id="f-pagador">${getTravelerOptions(p('pagador'))}</select></div>
    </div>`;

  if (type === 'hotel') body = `
    <div class="fg"><label>Nome</label><input id="f-nome" value="${p('nome')}"></div>
    <div class="r2">
      <div class="fg"><label>Cidade</label><input id="f-cidade" value="${p('cidade')}"></div>
      <div class="fg"><label>Site</label><input id="f-site" value="${p('site')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Check-in</label><input type="date" id="f-checkin" value="${p('checkin')}"></div>
      <div class="fg"><label>Check-out</label><input type="date" id="f-checkout" value="${p('checkout')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Horário check-in</label><input type="time" id="f-checkin-time" value="${p('checkinTime')}"></div>
      <div class="fg"><label>Horário check-out</label><input type="time" id="f-checkout-time" value="${p('checkoutTime')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Preço (${cur})</label><input type="text" inputmode="numeric" id="f-preco"
        value="${p('preco') ? fmtMask(Number(p('preco'))) : ''}" oninput="window.__onBudgetInput(this)" placeholder="0,00"></div>
      <div class="fg"><label>Quem pagou</label><select id="f-pagador">${getTravelerOptions(p('pagador'))}</select></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Status</label><select id="f-status">
        ${STATUS_OPTIONS.map(s =>
          `<option value="${s}" ${p('status')===s?'selected':''}>${s.charAt(0).toUpperCase()+s.slice(1)}</option>`).join('')}
      </select></div>
      <div class="fg"><label>Código reserva</label><input id="f-reserva" value="${p('reserva')}"></div>
    </div>`;

  if (type === 'agenda') body = `
    <div class="fg"><label>Título / Atração</label><input id="f-title" value="${p('title')}"></div>
    <div class="r2">
      <div class="fg"><label>Categoria</label>
        <select id="f-agenda-cat">
          ${AGENDA_CATS.map(c => `<option value="${c}" ${p('cat')===c?'selected':''}>${AGENDA_CAT_ICONS[c]??''} ${c}</option>`).join('')}
        </select>
      </div>
      <div class="fg"><label>Status</label>
        <select id="f-agenda-status">
          ${AGENDA_STATUS.map(s => `<option value="${s}" ${p('status',p('done')==='true'?'feito':'pendente')===s?'selected':''}>${s.charAt(0).toUpperCase()+s.slice(1)}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="r2">
      <div class="fg"><label>Data</label><input type="date" id="f-date" value="${p('date')}"></div>
      <div class="fg"><label>Horário</label><input type="time" id="f-time" value="${p('time')}"></div>
    </div>
    <div class="fg"><label>Local / Endereço</label><input id="f-local" value="${p('local')}"></div>
    <div class="r2">
      <div class="fg"><label>Valor (${cur})</label><input type="text" inputmode="numeric" id="f-preco"
        value="${p('preco') ? fmtMask(Number(p('preco'))) : ''}" oninput="window.__onBudgetInput(this)" placeholder="0,00"></div>
      <div class="fg"><label>Quem pagou</label><select id="f-pagador">${getTravelerOptions(p('pagador'))}</select></div>
    </div>
    <div class="fg"><label>Notas</label><textarea id="f-notes">${p('notes')}</textarea></div>`;

  if (type === 'check') body = `
    <div class="fg"><label>Descrição</label><input id="f-texto" value="${p('texto')}"></div>
    <div class="fg"><label>Categoria</label><select id="f-cat">
      ${CHECK_CATS.map(c =>
        `<option value="${c}" ${p('cat')===c?'selected':''}>${c}</option>`).join('')}
    </select></div>`;

  if (type === 'cotacao') {
    const cat     = (p('cat') || 'hotel') as CotacaoCat;
    const catOpts = (['hotel','ingresso','outro','transporte'] as CotacaoCat[]).map(c =>
      `<option value="${c}" ${cat===c?'selected':''}>${
        {hotel:'Hotel',transporte:'Transporte',ingresso:'Ingresso / Atração',outro:'Outro'}[c]
      }</option>`).join('');
    const statOpts = ['confirmado','descartado','em análise'].map(s =>
      `<option value="${s}" ${p('status')===s?'selected':''}>${s.charAt(0).toUpperCase()+s.slice(1)}</option>`).join('');
    body = `
      <div class="r2">
        <div class="fg"><label>Nome</label><input id="f-nome" value="${p('nome')}"></div>
        <div class="fg"><label>Categoria</label>
          <select id="f-cotacao-cat" onchange="window.__updateCotacaoFields()">${catOpts}</select></div>
      </div>
      <div class="r2">
        <div class="fg"><label>Preço (${cur})</label><input type="text" inputmode="numeric" id="f-preco"
          value="${p('preco') ? fmtMask(Number(p('preco'))) : ''}" oninput="window.__onBudgetInput(this)" placeholder="0,00"></div>
        <div class="fg"><label>Status</label><select id="f-cotacao-status">${statOpts}</select></div>
      </div>
      <div id="cotacao-extra-fields">${buildCotacaoExtraFields(cat, (f) => p(f))}</div>
      <div class="fg"><label>Notas</label><textarea id="f-notas">${p('notas')}</textarea></div>`;
  }

  if (type === 'compra') body = `
    <div class="fg"><label>Descrição</label><input id="f-descricao" value="${p('descricao')}"></div>
    <div class="r2">
      <div class="fg"><label>Marca</label><input id="f-marca" value="${p('marca')}"></div>
      <div class="fg"><label>Quantidade</label><input id="f-qty" value="${p('qty') || '1'}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Categoria</label><select id="f-compra-cat">
        ${COMPRA_CATS.map(c =>
          `<option value="${c}" ${p('cat')===c?'selected':''}>${c}</option>`).join('')}
      </select></div>
      <div class="fg"><label>Loja</label><input id="f-loja" value="${p('loja')}"></div>
    </div>
    <div class="fg"><label>Notas</label><input id="f-notas" value="${p('notas')}"></div>`;

  if (type === 'traveler') {
    const usersSnap = await get(ref(db, 'users'));
    type UserRecord = { displayName?: string; email: string };
    const allUsers  = (usersSnap.val() ?? {}) as Record<string, UserRecord>;
    const sorted    = Object.keys(allUsers).sort((a, b) =>
      (allUsers[a].displayName || allUsers[a].email)
        .localeCompare(allUsers[b].displayName || allUsers[b].email, 'pt-BR'));
    const opts = `<option value="">— Não vinculado —</option>` +
      sorted.map(u =>
        `<option value="${u}" ${p('uid')===u?'selected':''}>${sanitize(allUsers[u].displayName||allUsers[u].email)} (${sanitize(allUsers[u].email)})</option>`
      ).join('');
    body = `
      <div class="fg"><label>Nome</label><input id="f-name" value="${p('name')}"></div>
      <div class="fg"><label>Vincular usuário</label>
        <select id="f-uid" onchange="window.__autoFillTravelerName(this)">${opts}</select>
      </div>`;
  }

  uiOpenModal((isEdit ? 'Editar ' : 'Adicionar ') + (LABELS[type] ?? type), body, async () => {
    const tripId = getState().currentTripId!;
    const id     = editId ?? uid();
    const attachments = (item?.attachments ?? {}) as Record<string, Attachment>;

    if (type === 'destino')
      await saveTrip(`destinos/${id}`, { id, cidade: g('f-cidade'), pais: g('f-pais'), chegada: g('f-chegada'), saida: g('f-saida'), notas: g('f-notas') });
    if (type === 'transporte')
      await saveTrip(`transporte/${id}`, { id, tipo: g('f-tipo'), status: g('f-status'), origem: g('f-origem'), destino: g('f-destino'), data: g('f-data'), dataChegada: g('f-data-chegada'), horaPartida: g('f-hora-partida'), horaChegada: g('f-hora-chegada'), operadora: g('f-operadora'), num: g('f-num'), preco: gf('f-preco'), pagador: g('f-pagador'), attachments });
    if (type === 'hotel') {
      const ci = g('f-checkin'), co = g('f-checkout');
      await saveTrip(`hoteis/${id}`, { id, nome: g('f-nome'), cidade: g('f-cidade'), site: g('f-site'), checkin: ci, checkout: co, checkinTime: g('f-checkin-time'), checkoutTime: g('f-checkout-time'), noites: diffDays(ci, co), preco: gf('f-preco'), pagador: g('f-pagador'), status: g('f-status'), reserva: g('f-reserva'), attachments });
    }
    if (type === 'agenda') {
      const agendaStatus = g('f-agenda-status') as import('@/types').AgendaStatus;
      await saveTrip(`agenda/${id}`, { id, title: g('f-title'), date: g('f-date'), time: g('f-time'), local: g('f-local'), preco: gf('f-preco'), pagador: g('f-pagador'), notes: g('f-notes'), cat: g('f-agenda-cat') as import('@/types').AgendaCat, status: agendaStatus, done: agendaStatus === 'feito', attachments });
    }
    if (type === 'check')
      await saveTrip(`checks/${id}`, { id, texto: g('f-texto'), cat: g('f-cat'), done: item?.done ?? false });
    if (type === 'cotacao') {
      const ci = g('f-cotacao-checkin') || g('f-checkin');
      const co = g('f-cotacao-checkout') || g('f-checkout');
      await saveTrip(`cotacoes/${id}`, { id, nome: g('f-nome'), cat: g('f-cotacao-cat'), preco: gf('f-preco'), status: g('f-cotacao-status'), notas: g('f-notas'), cidade: g('f-cidade'), site: g('f-site'), checkin: ci, checkout: co, checkinTime: g('f-checkin-time'), checkoutTime: g('f-checkout-time'), noites: diffDays(ci, co), reserva: g('f-reserva'), tipo: g('f-tipo'), origem: g('f-origem'), destino: g('f-destino'), data: g('f-data'), dataChegada: g('f-data-chegada'), horaPartida: g('f-hora-partida'), horaChegada: g('f-hora-chegada'), operadora: g('f-operadora'), num: g('f-num'), local: g('f-local'), date: g('f-date'), createdAt: (item?.createdAt as number) ?? Date.now() });
    }
    if (type === 'compra')
      await saveTrip(`compras/${id}`, { id, descricao: g('f-descricao'), marca: g('f-marca'), qty: g('f-qty') || '1', loja: g('f-loja'), cat: g('f-compra-cat'), notas: g('f-notas'), done: item?.done ?? false });
    if (type === 'traveler') {
      const linkedUid = g('f-uid');
      const snap      = linkedUid ? await get(ref(db, `users/${linkedUid}`)) : null;
      const sys       = snap?.val() as { displayName?: string; email?: string } | null;
      const obj       = { id, name: sys ? (sys.displayName ?? sys.email ?? '') : g('f-name'), email: sys?.email ?? '', uid: linkedUid || '' };
      await saveTrip(`travelers/${id}`, obj);
      logAction('traveler_added', `Traveler added: ${obj.name}`, { tripId, travelerUid: linkedUid || undefined });
      if (linkedUid) await set(ref(db, `userTrips/${linkedUid}/${tripId}`), true);
      if (item?.uid && item.uid !== linkedUid)
        await remove(ref(db, `userTrips/${String(item.uid)}/${tripId}`));
    }
    toastSuccess('Salvo com sucesso!');
    renderAll();
  });
}

// ── Attachment modal ──────────────────────────────────────────
export async function openAttachModal(section: string, itemId: string): Promise<void> {
  const trip    = getTrip();
  const tripId  = getState().currentTripId!;
  const coll    = trip[section as keyof Trip] as Record<string, { attachments?: Record<string, Attachment> }> | undefined;
  const existing = toArr(coll?.[itemId]?.attachments ?? {}) as Attachment[];

  const existingHtml = existing.map(a => `
    <div style="display:flex;align-items:center;gap:6px;padding:5px 0;border-bottom:0.5px solid var(--border)">
      <a href="${attachmentViewerUrl(a)}" target="_blank" rel="noopener noreferrer" class="attach-chip">📎 ${sanitize(a.name)}</a>
      <button class="icon-btn del" onclick="window.__delAttachment('${section}','${itemId}','${a.id}','${encodeURIComponent(a.path)}')">×</button>
    </div>`).join('');

  uiOpenModal('Anexos', `
    ${existing.length ? `<div style="margin-bottom:12px">${existingHtml}</div>` : ''}
    <div class="fg"><label>Adicionar arquivo</label>
      <input type="file" id="f-attach-file">
    </div>
    <div class="upload-progress" id="attach-progress">
      <div class="upload-progress-fill" id="attach-progress-fill"></div>
    </div>`,
    async () => {
      const file = (document.getElementById('f-attach-file') as HTMLInputElement)?.files?.[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) throw new Error('O arquivo não pode ultrapassar 20 MB.');
      const attId  = uid();
      const path   = `attachments/${tripId}/${section}/${itemId}/${attId}_${file.name}`;
      const sref   = sRef(storage, path);
      const fill   = document.getElementById('attach-progress-fill') as HTMLElement | null;
      const prog   = document.getElementById('attach-progress') as HTMLElement | null;
      if (prog) prog.style.display = 'block';

      await new Promise<void>((resolve, reject) => {
        const task = uploadBytesResumable(sref, file);
        task.on('state_changed',
          snap => { if (fill) fill.style.width = Math.round(snap.bytesTransferred / snap.totalBytes * 100) + '%'; },
          reject, resolve);
      });

      const url = await getDownloadURL(sref);
      await saveTrip(`${section}/${itemId}/attachments/${attId}`, { id: attId, name: file.name, url, path });
      toastSuccess('Arquivo enviado!');
      renderAll();
    });
}

export async function delAttachment(section: string, itemId: string, attId: string, encodedPath: string): Promise<void> {
  const { confirmModal } = await import('@/ui/modal');
  const ok = await confirmModal('Remover este anexo?');
  if (!ok) return;
  const path = decodeURIComponent(encodedPath);
  deleteObject(sRef(storage, path)).catch(() => void 0);
  await saveTrip(`${section}/${itemId}/attachments/${attId}`, null);
  toastSuccess('Anexo removido.');
  renderAll();
}

// ── Currencies list in viagem tab ────────────────────────────
function renderCurrenciesList(): void {
  const el   = document.getElementById('currencies-list');
  const trip = getTrip();
  if (!el) return;
  const currencies = (trip.currencies as string[] | undefined) ?? ['BRL'];
  el.innerHTML = currencies.map((code: string) => {
    const info = COMMON_CURRENCIES.find(c => c.code === code);
    return `<div class="currency-chip">
      ${info?.symbol ?? code} ${code}
      ${code !== 'BRL' ? `<button onclick="window.__removeCurrency('${code}')" style="background:none;border:none;color:var(--muted);cursor:pointer;padding:0;margin-left:4px;font-size:12px">×</button>` : ''}
    </div>`;
  }).join('');
  // Update gastos currency filter options
  const curFilter = document.getElementById('gastos-cur-filter') as HTMLSelectElement | null;
  if (curFilter) {
    const extra = currencies.map((code: string) => `<option value="${code}">${code}</option>`).join('');
    curFilter.innerHTML = '<option value="">Todas moedas</option>' + extra;
  }
}

async function openCurrencyModal(): Promise<void> {
  const { COMMON_CURRENCIES } = await import('@/constants');
  const trip     = getTrip();
  const active   = trip.currencies ?? ['BRL'];
  const opts     = COMMON_CURRENCIES
    .filter(c => !active.includes(c.code))
    .map(c => `<option value="${c.code}">${c.symbol} ${c.code} — ${c.label}</option>`)
    .join('');
  uiOpenModal('Adicionar moeda', `
    <div class="fg">
      <label>Moedas ativas</label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px">
        ${active.map((code: string) => `<span class="currency-chip">${code}</span>`).join('')}
      </div>
    </div>
    <div class="fg">
      <label>Adicionar moeda</label>
      <select id="f-new-currency">${opts || '<option value="">Todas já adicionadas</option>'}</select>
    </div>`,
    async () => {
      const newCode = g('f-new-currency');
      if (!newCode) return;
      const updated = [...new Set([...active, newCode])];
      await saveTrip('currencies', updated);
      toastSuccess('Moeda adicionada!');
      renderCurrenciesList();
    });
}

function toggleGastosFilters(): void {
  const panel = document.getElementById('gastos-filters-panel');
  const btn   = document.getElementById('gastos-filter-toggle');
  if (!panel) return;
  const open = panel.classList.toggle('open');
  if (btn) btn.setAttribute('aria-expanded', String(open));
}

// ── Register ALL globals ──────────────────────────────────────
export function registerAllGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  w['__openModal']             = openItemModal;
  w['__delItem']               = delItem;
  w['__openAttachModal']       = openAttachModal;
  w['__delAttachment']         = delAttachment;
  w['__toggleAgenda']          = toggleAgenda;
  w['__toggleAgendaDay']       = toggleAgendaDay;
  w['__toggleAgendaFilters']   = toggleAgendaFilters;
  w['__setAgendaView']         = setAgendaView;
  w['__filterAgenda']          = filterAgenda;
  w['__clearAgendaFilters']    = clearAgendaFilters;
  w['__toggleGastosFilters']   = toggleGastosFilters;
  w['__toggleDestinoTrips']    = toggleDestinoTrips;
  w['__toggleCheck']           = toggleCheck;
  w['__toggleCompra']          = toggleCompra;
  w['__toggleCompare']         = toggleCompare;
  w['__clearCompare']          = clearCompare;
  w['__openCompareModal']      = openCompareModal;
  w['__updateCotacaoFields']   = updateCotacaoFields;
  w['__renderCotacoes']        = renderCotacoes;
  w['__renderCompras']         = renderCompras;
  w['__switchDocsTab']         = switchDocsTab;
  w['__openDocModal']          = openDocModal;
  w['__delGlobalDoc']          = delGlobalDoc;
  w['__openDocUrl']            = openDocUrl;
  w['__autoFillTravelerName']  = autoFillTravelerName;
  w['__saveBudgetLimit']       = saveBudgetLimit;
  w['__saveTripField']         = saveTripField;
  w['__onBudgetInput']         = onBudgetInput;
  w['__openGastoModal']        = openGastoModal;
  w['__filterGastos']          = filterGastos;
  w['__clearGastosFilters']    = clearGastosFilters;
  w['__switchSubTab']          = switchSubTab;
  w['__fabAction']             = fabAction;
  w['__openCurrencyModal']     = openCurrencyModal;
  w['__openDayTripModal']      = openDayTripModal;
  w['__delDayTrip']            = delDayTrip;
  w['__generateInviteCode']    = generateInviteCode;
  w['__revokeInviteCode']      = revokeInviteCode;
  w['__copyInviteCode']        = copyInviteCode;
  w['__joinWithCode']          = joinWithCode;
  registerExportGlobals();

  // Register lazy-render callbacks: when switching to a dirty tab, re-render it
  ALL_TABS.forEach(tabId => {
    onTabActivate(tabId as import('@/types').TabId, () => {
      if (_dirty.has(tabId)) renderForTab(tabId);
    });
  });
}
